import { useState, useEffect, useCallback } from 'react';
import { supabaseWithAuth } from '@/utils/supabaseWrapper';
import { logger } from '@/utils/productionLogger';

/**
 * Hook useCredits - Gestion du solde de crédits avec Realtime
 * 
 * Ce hook gère:
 * - La récupération du solde de crédits disponibles
 * - Les mises à jour en temps réel via Supabase Realtime
 * - La synchronisation entre onglets
 * 
 * Requirements: 6.1, 6.2, 6.5
 */
export const useCredits = (organizationId?: string) => {
  const [availableCredits, setAvailableCredits] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCreditBalance = useCallback(async (orgId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Récupérer le solde actuel depuis la table organizations avec protection auth
      // Utilise available_credits (nouvelle colonne du système de crédits)
      const { data: orgData, error: orgError } = await supabaseWithAuth.executeWithRetry(async () => {
        return supabaseWithAuth
          .from('organizations')
          .select('available_credits')
          .eq('id', orgId)
          .single();
      }, 'credit_balance.fetch');

      if (orgError) {
        throw orgError;
      }

      const currentBalance = (orgData as any)?.available_credits ?? 0;
      setAvailableCredits(currentBalance);

      logger.debug('Credit balance fetched', {
        orgId,
        availableCredits: currentBalance
      });

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      logger.error('Error fetching credit balance:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Vérifier si on peut consommer des crédits
  const hasCredits = (amount: number = 1): boolean => {
    return availableCredits >= amount;
  };

  // Auto-fetch quand l'organization change
  useEffect(() => {
    if (organizationId) {
      fetchCreditBalance(organizationId);
    }
  }, [organizationId, fetchCreditBalance]);

  // Écouter les changements en temps réel
  // Requirement 6.2: Mise à jour en temps réel via Supabase Realtime
  // Requirement 6.5: Synchronisation entre onglets
  useEffect(() => {
    if (!organizationId) return;

    logger.debug('Setting up realtime subscription for credits', { organizationId });

    const channel = supabaseWithAuth.client
      .channel(`credit_balance_${organizationId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'organizations',
          filter: `id=eq.${organizationId}`
        },
        (payload) => {
          logger.debug('Organization credit balance changed:', payload);
          if (payload.new && 'available_credits' in payload.new) {
            setAvailableCredits(payload.new.available_credits as number);
          }
        }
      )
      .subscribe();

    return () => {
      logger.debug('Cleaning up realtime subscription for credits');
      supabaseWithAuth.client.removeChannel(channel);
    };
  }, [organizationId]);

  return {
    availableCredits,
    loading,
    error,
    hasCredits,
    refreshBalance: () => organizationId && fetchCreditBalance(organizationId)
  };
};

// Export principal - pas d'alias redondant
